import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart2, TrendingUp, DollarSign } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './DividendCalculatorPage/components/CalculationForm';
import ResultsOverview from './DividendCalculatorPage/components/ResultsOverview';
import PaymentSchedule from './DividendCalculatorPage/components/PaymentSchedule';
import QuickAdjustmentSidebar from './DividendCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './DividendCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './DividendCalculatorPage/components/TipsSidebar';
import InfoContent from './DividendCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  // Основные параметры
  stockPrice: '1000', // Цена акции в рублях
  sharesCount: '100', // Количество акций
  dividendPerShare: '50', // Дивиденд на акцию в рублях
  dividendFrequency: 'quarterly', // Периодичность выплат: 'annually', 'semi_annually', 'quarterly', 'monthly'
  currency: 'RUB', // Валюта: 'RUB', 'USD', 'EUR'
  
  // Налоговые параметры
  taxRate: '13', // Ставка НДФЛ для резидентов РФ (13%)
  taxResidency: 'resident', // Налоговый резидент: 'resident', 'non_resident'
  hasDoubleTaxTreaty: false, // Есть ли соглашение об избежании двойного налогообложения
  foreignWithholdingTax: '10', // Ставка налога, удерживаемого в стране источника дивидендов
  
  // Параметры роста и реинвестирования
  includeDividendGrowth: false, // Учитывать ли рост дивидендов
  dividendGrowthRate: '5', // Годовой процент роста дивидендов
  includeReinvestment: false, // Учитывать ли реинвестирование дивидендов
  reinvestmentRate: '100', // Процент реинвестирования (от 0 до 100)
  
  // Параметры прогнозирования
  projectionPeriod: '5', // Период прогнозирования (лет)
  includeStockGrowth: false, // Учитывать ли рост стоимости акций
  stockGrowthRate: '7', // Годовой процент роста стоимости акций
  
  // Дата начала инвестирования
  startDate: new Date().toISOString().split('T')[0], // Текущая дата
};

// Определение типов периодичности выплат
export const dividendFrequencyTypes = [
  { id: 'annually', name: 'Ежегодно', periodsPerYear: 1 },
  { id: 'semi_annually', name: 'Раз в полгода', periodsPerYear: 2 },
  { id: 'quarterly', name: 'Ежеквартально', periodsPerYear: 4 },
  { id: 'monthly', name: 'Ежемесячно', periodsPerYear: 12 }
];

// Определение типов налоговых резиденств
export const taxResidencyTypes = [
  { id: 'resident', name: 'Налоговый резидент РФ', defaultRate: 13 },
  { id: 'non_resident', name: 'Налоговый нерезидент РФ', defaultRate: 15 }
];

// Определение валют
export const currencyTypes = [
  { id: 'RUB', name: 'Российский рубль (₽)', symbol: '₽' },
  { id: 'USD', name: 'Доллар США ($)', symbol: '$' },
  { id: 'EUR', name: 'Евро (€)', symbol: '€' }
];

/**
 * DividendCalculatorPage Component - Полный калькулятор дивидендов с учетом налогов и прогнозами.
 */
const DividendCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('dividend-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор дивидендов | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный калькулятор дивидендов онлайн: расчет потенциального дохода от ценных бумаг с учетом налогов, реинвестирования, роста дивидендов и стоимости акций.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedPortfolios, setSavedPortfolios] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных портфелей из localStorage ---
  useEffect(() => {
    const savedPortfoliosData = localStorage.getItem('savedPortfolios');
    if (savedPortfoliosData) {
      try {
        setSavedPortfolios(JSON.parse(savedPortfoliosData));
      } catch (e) {
        console.error("Error loading saved portfolios:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'russian':
          newState.currency = 'RUB';
          newState.taxResidency = 'resident';
          break;
        case 'foreign':
          newState.currency = 'USD';
          newState.hasDoubleTaxTreaty = true;
          break;
        case 'growth':
          newState.includeDividendGrowth = true;
          newState.includeStockGrowth = true;
          break;
        case 'reinvestment':
          newState.includeReinvestment = true;
          break;
        case 'tax-optimization':
          newState.hasDoubleTaxTreaty = true;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setSchedule([]);
    setErrors({});
    setShowFullSchedule(false);

  }, [subToolId]);

  // Сохранение портфеля в localStorage
  const savePortfolio = (name) => {
    if (!results) return;

    const newSavedPortfolio = {
      id: Date.now().toString(),
      name: name || `Портфель ${formData.sharesCount} акций по ${formData.stockPrice}₽`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      currency: formData.currency
    };

    const updatedSavedPortfolios = [...savedPortfolios, newSavedPortfolio];
    setSavedPortfolios(updatedSavedPortfolios);
    localStorage.setItem('savedPortfolios', JSON.stringify(updatedSavedPortfolios));

    return newSavedPortfolio;
  };

  // Удаление сохраненного портфеля
  const deleteSavedPortfolio = (id) => {
    const updatedSavedPortfolios = savedPortfolios.filter(portfolio => portfolio.id !== id);
    setSavedPortfolios(updatedSavedPortfolios);
    localStorage.setItem('savedPortfolios', JSON.stringify(updatedSavedPortfolios));
  };

  // Загрузка сохраненного портфеля
  const loadSavedPortfolio = (id) => {
    const portfolioToLoad = savedPortfolios.find(portfolio => portfolio.id === id);
    if (portfolioToLoad) {
      setFormData(portfolioToLoad.formData);
      setResults(portfolioToLoad.results);
      // Пересчитаем график выплат для визуализации
      // В реальном кейсе нужно будет либо хранить график в сохраненном портфеле,
      // либо заново вызвать расчетную функцию
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-green-600 dark:text-green-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор дивидендов'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              schedule={schedule}
              setSchedule={setSchedule}
              dividendFrequencyTypes={dividendFrequencyTypes}
              taxResidencyTypes={taxResidencyTypes}
              currencyTypes={currencyTypes}
              savePortfolio={savePortfolio}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <PaymentSchedule 
                  schedule={schedule} 
                  results={results}
                  showFullSchedule={showFullSchedule}
                  setShowFullSchedule={setShowFullSchedule}
                />
              </>
            )}

            {/* --- Информационный контент о дивидендах --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              currencyTypes={currencyTypes}
            />

            {/* --- Панель сравнения портфелей --- */}
            <ComparisonSidebar 
              savedPortfolios={savedPortfolios}
              deleteSavedPortfolio={deleteSavedPortfolio}
              loadSavedPortfolio={loadSavedPortfolio}
              currencyTypes={currencyTypes}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Payment Schedule Table */
        .payment-schedule-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .payment-schedule-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default DividendCalculatorPage;